// src/pages/Login.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isCreator, setIsCreator] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const country = 'DK';
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  // Data consent
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [personalizationEnabled, setPersonalizationEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [dataProcessingAccepted, setDataProcessingAccepted] = useState(false);

  const navigate = useNavigate();

  const handleForgotPassword = async () => {
    if (!email) { setError('Indtast din e-mail ovenfor først'); return; }
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setError('');
    } catch (err) {
      setError('Kunne ikke sende nulstillings-e-mail: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && password !== confirmPassword) { setError('Adgangskoderne stemmer ikke overens'); return; }
    if (!isLogin && !dataProcessingAccepted) { setError('Du skal acceptere databehandlingsaftalen for at oprette en konto'); return; }

    try {
      setError('');
      setLoading(true);
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/');
      } else {
        const { user } = await createUserWithEmailAndPassword(auth, email, password);
        await setDoc(doc(db, 'users', user.uid), {
          email,
          username,
          isCreator,
          dateJoined: new Date(),
          profilePicURL: '',
          bio: '',
          favoriteCategories: [],
          followedPodcasts: [],
          isEmailVerified: false,
          listeningHistory: [],
          dateOfBirth: dateOfBirth || null,
          country,
          preferredLanguage: 'da',
          dataConsent: {
            analyticsEnabled,
            personalizationEnabled,
            marketingEnabled,
            dataProcessingAccepted,
            lastUpdated: new Date(),
          },
          notificationPreferences: {
            newEpisodes: true,
            creatorUpdates: true,
            recommendations: true,
            systemUpdates: true,
          },
        });
        await sendEmailVerification(user);
        setVerificationSent(true);
      }
    } catch (err) {
      setError((isLogin ? 'Login mislykkedes: ' : 'Oprettelse mislykkedes: ') + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Shared field style ─────────────────────────────────────────── */
  const fieldStyle = {
    width: '100%',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-strong)',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 15,
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginBottom: 6,
  };
  const fieldWrap = { marginBottom: 16 };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>

        {/* Brand mark */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <a href="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span className="fc-brand-mark" aria-hidden="true" />
            <span className="fc-brand-name" style={{ fontSize: 22 }}>Faircast</span>
          </a>
        </div>

        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: 20,
          padding: 32,
        }}>
          {verificationSent ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
                Bekræft din e-mail
              </h2>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
                Vi har sendt en bekræftelses-e-mail til <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>.
                Klik på linket i e-mailen for at aktivere din konto.
              </p>
              <button
                onClick={() => navigate('/')}
                className="fc-cta-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Fortsæt til forsiden
              </button>
            </div>
          ) : (
          <>
          {/* Tab toggle */}
          <div style={{
            display: 'flex',
            background: 'var(--bg-elevated)',
            borderRadius: 10,
            padding: 4,
            marginBottom: 28,
          }}>
            {[
              { key: true,  label: 'Log ind' },
              { key: false, label: 'Opret konto' },
            ].map(tab => (
              <button
                key={String(tab.key)}
                onClick={() => { setIsLogin(tab.key); setError(''); setResetSent(false); }}
                style={{
                  flex: 1,
                  padding: '8px 0',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  transition: 'all 0.15s',
                  background: isLogin === tab.key ? 'var(--bg-card)' : 'transparent',
                  color: isLogin === tab.key ? 'var(--text-primary)' : 'var(--text-mute)',
                  boxShadow: isLogin === tab.key ? '0 1px 4px rgba(0,0,0,0.3)' : 'none',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.1)',
              border: '1px solid rgba(220,38,38,0.3)',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 14,
              color: '#f87171',
              marginBottom: 20,
            }}>{error}</div>
          )}

          {resetSent && (
            <div style={{
              background: 'rgba(34,197,94,0.1)',
              border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 14,
              color: '#4ade80',
              marginBottom: 20,
            }}>Nulstillings-e-mail sendt — tjek din indbakke.</div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={fieldWrap}>
              <label style={labelStyle} htmlFor="email">E-mail</label>
              <input id="email" type="email" required value={email}
                onChange={e => setEmail(e.target.value)} style={fieldStyle} />
            </div>

            {!isLogin && (
              <div style={fieldWrap}>
                <label style={labelStyle} htmlFor="username">Brugernavn</label>
                <input id="username" type="text" required value={username}
                  onChange={e => setUsername(e.target.value)} style={fieldStyle} />
              </div>
            )}

            <div style={fieldWrap}>
              <label style={labelStyle} htmlFor="password">Adgangskode</label>
              <input id="password" type="password" required value={password}
                onChange={e => setPassword(e.target.value)} style={fieldStyle} />
            </div>

            {!isLogin && (
              <>
                <div style={fieldWrap}>
                  <label style={labelStyle} htmlFor="confirmPassword">Bekræft adgangskode</label>
                  <input id="confirmPassword" type="password" required value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)} style={fieldStyle} />
                </div>

                <div style={fieldWrap}>
                  <label style={labelStyle} htmlFor="dateOfBirth">Fødselsdato (valgfrit)</label>
                  <input id="dateOfBirth" type="date" value={dateOfBirth}
                    onChange={e => setDateOfBirth(e.target.value)} style={fieldStyle} />
                </div>

                <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input id="isCreator" type="checkbox" checked={isCreator}
                    onChange={e => setIsCreator(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: 'var(--primary-color)', flexShrink: 0 }} />
                  <label htmlFor="isCreator" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                    Jeg er podcast-skaber
                  </label>
                </div>

                {/* Data consent */}
                <div style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 12,
                  padding: '16px 18px',
                  marginBottom: 20,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                    Datasamtykke
                  </div>
                  {[
                    { id: 'analytics', checked: analyticsEnabled, setter: setAnalyticsEnabled, label: 'Anonymiseret brugsdata for at forbedre platformen' },
                    { id: 'personalization', checked: personalizationEnabled, setter: setPersonalizationEnabled, label: 'Personlige anbefalinger' },
                    { id: 'marketing', checked: marketingEnabled, setter: setMarketingEnabled, label: 'Nyhedsbrev og marketings-e-mails' },
                  ].map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <input id={c.id} type="checkbox" checked={c.checked}
                        onChange={e => c.setter(e.target.checked)}
                        style={{ width: 15, height: 15, accentColor: 'var(--primary-color)', flexShrink: 0 }} />
                      <label htmlFor={c.id} style={{ fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1.4 }}>
                        {c.label}
                      </label>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-color)' }}>
                    <input id="dataProcessing" type="checkbox" required checked={dataProcessingAccepted}
                      onChange={e => setDataProcessingAccepted(e.target.checked)}
                      style={{ width: 15, height: 15, accentColor: 'var(--primary-color)', flexShrink: 0, marginTop: 2 }} />
                    <label htmlFor="dataProcessing" style={{ fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1.4 }}>
                      Jeg accepterer <a href="/terms" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>vilkår og betingelser</a> og <a href="/privacy" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>privatlivspolitikken</a>. <span style={{ color: 'var(--primary-color)' }}>*</span>
                    </label>
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="fc-cta-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: 4, opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Vent…' : isLogin ? 'Log ind' : 'Opret konto'}
            </button>
          </form>

          {isLogin && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                onClick={handleForgotPassword}
                style={{ background: 'none', border: 'none', color: 'var(--text-mute)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Glemt adgangskode?
              </button>
            </div>
          )}
          </>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-mute)' }}>
          Har du ikke en konto endnu?{' '}
          <a href="/subscribe" style={{ color: 'var(--primary-color)', textDecoration: 'none', fontWeight: 600 }}>
            Tilmeld dig her
          </a>
        </p>
      </div>
    </div>
  );
}

export default Login;
